/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import React, { useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import styled, { withTheme } from 'styled-components';
import { selectPerformanceOutlookLegendPanelShown } from '../../../store';
import { MapActionsContext, useSelectorWithMapId } from '../map/BaseMapContainer';
import GoogleMapsControl, { MapControlOrderIndex } from '../map/GoogleMapsControl';
import StyledProps from '../theme/AppThemeInterface';

interface PerformanceOutlookLegendPanelControlProps extends StyledProps {
  map: google.maps.Map;
  isFullScreen: boolean;
}

export function PerformanceOutlookLegendPanelControl({
  theme,
  map,
  isFullScreen,
}: PerformanceOutlookLegendPanelControlProps) {
  const dispatch = useDispatch();
  const overlaysPanelIcon = useRef<HTMLElement>();
  const mapActionsContext = useContext(MapActionsContext);
  const performanceOutlookLegendPanelShown = useSelectorWithMapId(selectPerformanceOutlookLegendPanelShown);
  const { togglePerformanceOutlookLegendPanel } = mapActionsContext.mapActions;

  useLayoutEffect(() => {
    if (overlaysPanelIcon.current && overlaysPanelIcon.current.classList) {
      if (performanceOutlookLegendPanelShown) {
        overlaysPanelIcon.current.classList.add('performance_outlook_legend_panel_icon--active');
      } else {
        overlaysPanelIcon.current.classList.remove('performance_outlook_legend_panel_icon--active');
      }
    }
  }, [performanceOutlookLegendPanelShown]);

  const items = useMemo(
    () => [
      {
        render: () => <StyledInfoRoundedIcon id="performance_outlook_legend_panel_button" ref={overlaysPanelIcon} />,
        buttonTextColor: theme.secondaryButtonTextColor,
        id: 'performance_outlook_legend_panel_button_container',
        onClick: () => {
          dispatch(togglePerformanceOutlookLegendPanel());
        },
      },
    ],
    [dispatch, overlaysPanelIcon, theme, togglePerformanceOutlookLegendPanel],
  );

  return (
    <GoogleMapsControl
      items={items}
      map={map}
      mapPosition={google.maps.ControlPosition.RIGHT_TOP}
      height={32}
      hidden={isFullScreen}
      index={MapControlOrderIndex.PERFORMANCE_OUTLOOK_LEGEND_CONTOL}
    />
  );
}

export default withTheme(PerformanceOutlookLegendPanelControl);

const StyledInfoRoundedIcon = styled(InfoRoundedIcon)`
  width: 20px !important;
`;
